import { MRT_Row } from "material-react-table";
import type { Job } from "../../../../../../api/types";
import { ColumnContext } from "../ColumnContext";
import { DateColumnViewModel } from "./dateColumn.types";
import { dateToInputString, inputStringToDate } from "./dateColumn.helpers";

/**
 * Hook that provides the DateColumn ViewModel
 * @param context - The column context containing state and helpers
 * @returns ViewModel with all business logic functions
 */
export function useDateColumnViewModel(context: ColumnContext): DateColumnViewModel {
	/**
	 * Get current date value as input string from job
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getCurrentDateValue(job: Job, fieldKey: keyof Job): string {
		const currentDate = job?.[fieldKey] as Date | undefined;
		return dateToInputString(currentDate);
	}

	/**
	 * Handle individual date change (updates job value)
	 */
	function handleDateChange(jobId: string, fieldKey: keyof Job, dateString: string): void {
		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;

		const index = newJobs.indexOf(newJob);
		(newJob as any)[fieldKey] = inputStringToDate(dateString);
		newJobs[index] = newJob;
		context.setJobs(newJobs);
	}

	/**
	 * Handle individual date blur (validates and updates row cache if row provided)
	 * Desktop passes row for MRT cache updates; mobile omits it
	 */
	function handleDateBlur(job: Job, fieldKey: keyof Job, row?: MRT_Row<Job>): void {
		// Update row cache for MRT compatibility (desktop only)
		if (row) {
			(row._valuesCache as any)[fieldKey] = job[fieldKey];
		}
		context.validateJobs([job], { updateState: true, clearPrevious: false });
	}

	/**
	 * Handle group date change
	 */
	function handleGroupDateChange(groupValue: string, fieldKey: keyof Job, dateString: string): void {
		const newDate = inputStringToDate(dateString);
		const newJobs = [...context.jobs];

		for (const job of newJobs) {
			if (job.importGrowerName === groupValue) {
				(job as any)[fieldKey] = newDate;
			}
		}

		context.setJobs(newJobs);
		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	return {
		getCurrentDateValue,
		handleDateChange,
		handleDateBlur,
		handleGroupDateChange,
	};
}
